import React, { useState } from "react";
import { motion, Reorder } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/pro-solid-svg-icons";
import LoadingSpinner from "../loading/LoadingSpinner";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";

interface ImageUploadProps {
  image: {
    id?: number;
    file?: File;
    link?: string;
    alt: string;
    fileName: string;
    order_position: number;
    tag_text?: string;
    tag_color?: string;
  };
  index: number;
  onRemove: (index: number) => void;
  onAltChange: (index: number, alt: string) => void;
  onTagChange: (index: number, tag_text: string, tag_color: string) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  image,
  index,
  onRemove,
  onAltChange,
  onTagChange,
}) => {
  const [alt, setAlt] = useState(image.alt);
  const [tagText, setTagText] = useState(image.tag_text || "");
  const [tagColor, setTagColor] = useState(image.tag_color || "#000000");
  const [hexInput, setHexInput] = useState(tagColor);

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHexInput(value);

    // Only update the actual color if it's a valid hex code
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      setTagColor(value);
      onTagChange(index, tagText, value);
    }
  };

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTagColor(value);
    setHexInput(value);
    onTagChange(index, tagText, value);
  };

  return (
    <Reorder.Item
      value={image}
      id={image.id?.toString() || image.fileName}
      className="relative bg-base-300 p-4 rounded-lg"
    >
      <div className="flex items-start gap-4">
        <div className="flex items-center gap-2">
          <motion.div className="cursor-move text-gray-400 hover:text-gray-200">
            <FontAwesomeIcon icon={faGripVertical} />
          </motion.div>
          <span className="font-bold text-gray-400">{index + 1}</span>
        </div>

        <div className="relative w-32 h-32 flex-shrink-0">
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
          <img
            src={
              image.link || (image.file ? URL.createObjectURL(image.file) : "")
            }
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover rounded-lg"
          />
        </div>

        <div className="flex-grow space-y-3">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-300">
              Alt Text
            </label>
            <textarea
              value={alt}
              onChange={(e) => {
                setAlt(e.target.value);
                onAltChange(index, e.target.value);
              }}
              placeholder="Enter image description"
              className="textarea textarea-bordered bg-base-200 h-16 resize-none"
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-start gap-4">
              <div className="flex-grow">
                <label className="text-sm font-medium text-gray-300">Tag</label>
                <input
                  type="text"
                  value={tagText}
                  onChange={(e) => {
                    setTagText(e.target.value);
                    onTagChange(index, e.target.value, tagColor);
                  }}
                  placeholder="Enter tag text"
                  className="input input-bordered bg-base-200 w-full mt-1"
                />
              </div>
              <div className="flex-shrink-0">
                <label className="text-sm font-medium text-gray-300">
                  Tag Color
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={tagColor}
                    onChange={handleColorPickerChange}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={hexInput}
                    onChange={handleHexInputChange}
                    placeholder="#000000"
                    className="input input-bordered bg-base-200 w-24 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={() => onRemove(index)}
          className="absolute -top-2 -right-2 rounded-full p-1 bg-gray-600 hover:bg-gray-500 transition-colors w-8 h-8"
        >
          <FontAwesomeIcon icon={faTrash} className="w-3 h-3" />
        </button>
      </div>
    </Reorder.Item>
  );
};

export default ImageUpload;
